// [PURPOSE]: Unified analytics service wrapping Firebase Analytics.
// [WHY]: Single import point for all analytics calls. Silently no-ops if
//        Firebase Analytics isn't initialized (missing measurementId or unsupported browser).
//        This means components can always call trackEvent() without guards.
// [INTEGRATION]: Components import { trackEvent } from '../services/analytics';
//               Firebase Analytics init happens in src/firebase/config.js.
// [CREATED]: Session 6 — Observability Wiring (April 2026)

import { analytics, logEvent, setUserProperties } from '../firebase/config';

/**
 * Track an analytics event. Silently no-ops if Analytics is not initialized.
 *
 * @param {string} name — event name (snake_case, e.g. 'character_created')
 * @param {Object} [params={}] — event parameters (no PII — no emails, no character names)
 */
export const trackEvent = (name, params = {}) => {
  try {
    if (analytics) {
      logEvent(analytics, name, params);
    }
  } catch {
    // [PURPOSE]: Non-fatal. Analytics failure must never break the app.
    // Common causes: ad blockers, privacy browsers, Capacitor WebView restrictions.
  }
};

/**
 * Set user properties for analytics segmentation.
 * Called once after auth state changes.
 *
 * @param {Object} properties — key/value pairs (no PII)
 */
export const setAnalyticsUserProperties = (properties) => {
  try {
    if (analytics) {
      setUserProperties(analytics, properties);
    }
  } catch {
    // Non-fatal
  }
};

/**
 * Identify user for analytics (called on sign-in).
 * Sets isPremium and platform properties for segmentation.
 *
 * @param {Object} user — { isPremium: boolean }
 */
export const identifyUser = (user) => {
  setAnalyticsUserProperties({
    is_premium: user?.isPremium ? 'true' : 'false',
    platform: window.Capacitor ? 'native' : 'web',
    app_version: (() => {
      try {
        // Dynamic import would be cleaner but this is called rarely
        return require('../constants/version').APP_VERSION;
      } catch {
        return 'unknown';
      }
    })(),
  });
};
